import { useCallback, useMemo } from "react";

import { useAudienceActions, useAudienceContext } from "@/context";
import { Audience, AudienceSegment } from "@/types";
import { defaultSegment } from "@/utils";

/**
 * The selected segment of an audience, shared by its pages through the audience
 * context so the insight and export pages show the same one. Defaults to the
 * first segment, and `index` is its position in the insight and size arrays.
 */
export const useSegmentSelection = (audience: Audience | null | undefined) => {
  const { currentSegment } = useAudienceContext();
  const { selectSegment } = useAudienceActions();

  const segments = audience?.segments;

  const { segment, index } = useMemo(() => {
    if (!segments || segments.length === 0) return { segment: defaultSegment, index: 0 };

    // Ignore a segment selected for another audience
    const selected = currentSegment
      ? segments.findIndex((s) => s.segment_id === currentSegment.segment_id)
      : -1;
    return selected >= 0
      ? { segment: segments[selected], index: selected }
      : { segment: segments[0], index: 0 };
  }, [currentSegment, segments]);

  /** Selects by name, e.g. from a SegmentBox select */
  const selectByName = useCallback((name: string) => {
    const match = segments?.find((s) => s.segment_name === name);
    if (match) selectSegment(match);
  }, [segments, selectSegment]);

  const select = useCallback((selected: AudienceSegment) => selectSegment(selected), [selectSegment]);

  return useMemo(() => ({ segment, index, select, selectByName }), [segment, index, select, selectByName]);
};
